// v3.3.366 — WhatsApp chat popup modal
// يفتح محادثة عميل/مسوق محدد بالهاتف على إحدى قنوات الواتساب
// يعرض المحادثات السابقة + يسمح بإرسال رسالة جديدة
import React, { useState, useEffect, useRef } from "react";
import { whatsapp } from "../services/api";
import toast from "react-hot-toast";
import { FiX, FiSend, FiRefreshCw } from "react-icons/fi";

// تطبيع الهاتف: 05x → 9665x، إزالة + ومسافات و -
function normalizePhone(phone) {
  if (!phone) return "";
  let p = String(phone).replace(/[\s+\-()]/g, "");
  if (p.startsWith("00")) p = p.slice(2);
  if (p.startsWith("05")) p = "966" + p.slice(1);
  return p;
}

// استخراج جزء التاريخ والوقت من timestamp (ميلي ثانية)
function fmtTime(ts) {
  if (!ts) return "";
  const d = new Date(Number(ts));
  return d.toLocaleString("ar-SA", { hour: "2-digit", minute: "2-digit", day: "numeric", month: "short" });
}

// v3.3.605 — أسماء قنوات الواتساب المشبوكة في النظام (لعرض القناة/الرقم الفاعل)
const SESSION_LABELS = {
  corporate:    "قناة العملاء",
  marketers:    "قناة المسوقين",
  abubader2:    "أبو بدر 2",
  abubader_biz: "أبو بدر بزنس",
};

// v3.4.691 — quickTexts: [{ label, text, title? }] — أزرار فوق المؤلّف تضع نصًا جاهزًا في الحقل (يبقى على المستخدم ضغط إرسال)
export default function WhatsAppChatModal({ phone, name, session = "corporate", onClose, lockSession = false, quickTexts = [] }) {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [text, setText] = useState("");
  const [activeSession, setActiveSession] = useState(session);
  // v3.3.605 — الرقم/القناة المتصلة في النظام التي تجري منها المحادثة
  const [connInfo, setConnInfo] = useState({ connected: false, phone: null, label: "" });
  const scrollRef = useRef(null);

  const normalizedPhone = normalizePhone(phone);

  const loadMessages = async () => {
    setLoading(true);
    try {
      const r = await whatsapp.getInboxMessages(activeSession, normalizedPhone, 500);
      // v3.3.369 — backend يرجع DESC (الأحدث أولاً) — نعكس لعرض الأقدم في الأعلى والأحدث في الأسفل
      const msgs = (r.data.messages || []).slice().reverse();
      setMessages(msgs);
      setTimeout(() => {
        if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
      }, 80);
    } catch (e) {
      console.error(e);
      setMessages([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadMessages();
    // v3.3.369 — polling لتحديث الرسائل كل 30 ثانية
    const t = setInterval(() => loadMessages(), 30000);
    return () => clearInterval(t);
    /* eslint-disable-next-line */
  }, [activeSession, normalizedPhone]);

  // v3.3.605 — جلب حالة القناة المتصلة + رقمها الفاعل في النظام
  useEffect(() => {
    let alive = true;
    whatsapp.getStatus(activeSession)
      .then(r => { if (alive) setConnInfo({ connected: !!r.data.connected, phone: r.data.phone || null, label: r.data.label || "" }); })
      .catch(() => { if (alive) setConnInfo({ connected: false, phone: null, label: "" }); });
    return () => { alive = false; };
  }, [activeSession]);

  const handleSend = async () => {
    const msg = text.trim();
    if (!msg) return;
    setSending(true);
    try {
      await whatsapp.sendMessage(activeSession, normalizedPhone, msg, {
        recipientType: "client",
        recipientName: name || "",
      });
      setText("");
      toast.success("تم الإرسال");
      // أضف الرسالة محلياً قبل إعادة التحميل
      setMessages(m => [...m, { fromMe: true, body: msg, timestamp: Date.now(), _local: true }]);
      setTimeout(() => loadMessages(), 1500);
    } catch (e) {
      const why = e.response?.data?.error || "فشل الإرسال";
      toast.error(why);
      setMessages(m => [...m, { fromMe: true, failed: true, error: why, body: msg, timestamp: Math.floor(Date.now() / 1000), _local: true }]); // v3.4.704 — تبقى ظاهرة بالأحمر
    } finally {
      setSending(false);
    }
  };

  return (
    <div onClick={onClose}
      style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999, padding: 16 }}>
      <div onClick={e => e.stopPropagation()}
        style={{ background: "#fff", borderRadius: 12, width: "100%", maxWidth: 520, height: "82vh", maxHeight: 720, display: "flex", flexDirection: "column", overflow: "hidden", boxShadow: "0 20px 50px rgba(0,0,0,0.25)" }}>

        {/* Header */}
        <div style={{ background: "linear-gradient(135deg, #25d366 0%, #128c7e 100%)", color: "#fff", padding: "12px 16px", display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{ width: 38, height: 38, borderRadius: "50%", background: "rgba(255,255,255,0.25)", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, fontSize: 16 }}>
            {(name || "?").charAt(0)}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 700, fontSize: 14, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{name || "عميل"}</div>
            <div style={{ fontSize: 11, opacity: 0.9, fontFamily: "monospace", direction: "ltr" }}>{normalizedPhone}</div>
          </div>
          <button onClick={loadMessages} title="تحديث" disabled={loading}
            style={{ background: "rgba(255,255,255,0.2)", border: "none", borderRadius: 6, padding: 6, cursor: "pointer", color: "#fff" }}>
            <FiRefreshCw size={14} className={loading ? "spin" : ""} />
          </button>
          <button onClick={onClose}
            style={{ background: "rgba(255,255,255,0.2)", border: "none", borderRadius: 6, padding: 6, cursor: "pointer", color: "#fff" }}>
            <FiX size={16} />
          </button>
        </div>

        {/* v3.3.605 — القناة/الرقم الفاعل المشبوك في النظام لهذه المحادثة */}
        <div style={{ background: connInfo.connected ? "#f0fdf4" : "#fef2f2", borderBottom: `1px solid ${connInfo.connected ? "#dcfce7" : "#fecaca"}`, padding: "6px 14px", display: "flex", alignItems: "center", gap: 8, fontSize: 11 }}>
          <span style={{ width: 8, height: 8, borderRadius: "50%", background: connInfo.connected ? "#16a34a" : "#dc2626", flexShrink: 0 }} />
          <span style={{ color: connInfo.connected ? "#166534" : "#991b1b" }}>
            يُرسَل عبر: <strong>{SESSION_LABELS[activeSession] || connInfo.label || activeSession}</strong>
          </span>
          {connInfo.phone
            ? <span style={{ fontFamily: "monospace", direction: "ltr", marginInlineStart: "auto", color: connInfo.connected ? "#15803d" : "#991b1b", fontWeight: 700 }}>📱 {connInfo.phone}</span>
            : <span style={{ marginInlineStart: "auto", color: "#991b1b" }}>{connInfo.connected ? "" : "غير متصل"}</span>}
        </div>

        {/* Messages */}
        <div ref={scrollRef}
          style={{ flex: 1, overflowY: "auto", padding: 12, background: "#e5ddd5", backgroundImage: "linear-gradient(rgba(255,255,255,0.65), rgba(255,255,255,0.65))" }}>
          {loading ? (
            <div style={{ textAlign: "center", color: "#64748b", padding: 30, fontSize: 12 }}>⏳ جاري تحميل المحادثة...</div>
          ) : messages.length === 0 ? (
            <div style={{ textAlign: "center", color: "#64748b", padding: 30, fontSize: 12 }}>
              💬 لا توجد محادثة سابقة<br/>
              <span style={{ fontSize: 11, color: "#94a3b8" }}>ابدأ المحادثة بإرسال رسالة أدناه</span>
            </div>
          ) : (
            messages.map((m, i) => {
              const isMe = m.fromMe || m.from_me;
              const rawBody = m.body || m.message || m.text || "";
              const ts = m.timestamp || m.t || m.created_at;
              const msgType = m.type || m.message_type;
              // كشف base64 / مرفقات (سلسلة طويلة بدون مسافات أو علامات base64)
              const looksBase64 = rawBody.length > 150 && /^[A-Za-z0-9+/=_\-]+$/.test(rawBody.slice(0, 100).replace(/\s/g, ""));
              const isLikelyMedia =
                msgType === "image" || msgType === "video" || msgType === "audio" || msgType === "document" || msgType === "sticker" ||
                looksBase64 ||
                (rawBody.length > 250 && !/\s/.test(rawBody.slice(0, 250)));
              const body = isLikelyMedia ? "" : rawBody;
              return (
                <div key={i} style={{ display: "flex", justifyContent: isMe ? "flex-end" : "flex-start", marginBottom: 6 }}>
                  <div style={{
                    maxWidth: "75%",
                    background: m.failed ? "#fee2e2" : isMe ? "#dcf8c6" : "#fff", /* v3.4.704 */
                    border: m.failed ? "1px solid #fca5a5" : "none",
                    borderRadius: 8,
                    padding: "6px 10px",
                    fontSize: 13,
                    boxShadow: "0 1px 1px rgba(0,0,0,0.1)",
                    wordBreak: "break-word",
                    whiteSpace: "pre-wrap",
                  }}>
                    {isLikelyMedia ? (
                      <em style={{ color: "#64748b", fontSize: 12 }}>
                        📎 {msgType === "image" ? "صورة" : msgType === "video" ? "فيديو" : msgType === "audio" ? "تسجيل صوتي" : msgType === "document" ? "ملف" : msgType === "sticker" ? "ملصق" : "مرفق"}
                      </em>
                    ) : body ? body : (
                      <em style={{ color: "#94a3b8", fontSize: 11 }}>(رسالة فارغة)</em>
                    )}
                    {m.failed && <div style={{ fontSize: 11, color: "#b91c1c", fontWeight: 800, marginTop: 4 }}>⚠️ لم تُرسل — {m.error || "فشل الإرسال"}</div>}{/* v3.4.704 */}
                    <div style={{ fontSize: 9, color: "#64748b", textAlign: "left", marginTop: 2, direction: "ltr" }}>
                      {fmtTime(ts)}
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* v3.4.691 — نصوص جاهزة */}
        {quickTexts.length > 0 && (
          <div style={{ borderTop: "1px solid #e2e8f0", padding: "6px 10px 0", background: "#f8fafc", display: "flex", gap: 6, flexWrap: "wrap" }}>
            {quickTexts.map((q, i) => (
              <button key={i} type="button" disabled={!q.text} title={q.title || ""} onClick={() => { setText(q.text); setTimeout(() => { const el = document.getElementById("wa-chat-composer"); if (el) { el.focus(); el.scrollTop = 0; } }, 0); }}
                style={{ padding: "5px 11px", borderRadius: 16, border: "1px solid " + (q.text ? "#16a34a" : "#e2e8f0"), background: q.text ? "#dcfce7" : "#fff", color: q.text ? "#166534" : "#94a3b8", fontSize: 12, fontWeight: 700, cursor: q.text ? "pointer" : "not-allowed", fontFamily: "inherit" }}>
                {q.label}
              </button>
            ))}
          </div>
        )}
        {!connInfo.connected && <div style={{ background: "#fef2f2", color: "#991b1b", fontSize: 11.5, fontWeight: 700, padding: "6px 12px", borderTop: "1px solid #fecaca" }}>⛔ القناة «{SESSION_LABELS[activeSession] || activeSession}» غير متصلة الآن — لن تُرسل أي رسالة حتى يُعاد ربط الرقم من «إدارة الواتس»</div>}{/* v3.4.704 */}
        {/* Composer */}
        <div style={{ borderTop: "1px solid #e2e8f0", padding: 10, background: "#f8fafc", display: "flex", gap: 6 }}>
          <textarea id="wa-chat-composer" value={text} onChange={e => setText(e.target.value)}
            onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
            placeholder="اكتب رسالة..."
            rows={Math.min(8, Math.max(1, (text.match(/\n/g) || []).length + 1))}
            style={{ flex: 1, padding: "8px 12px", border: "1px solid #e2e8f0", borderRadius: 18, fontSize: 13, fontFamily: "inherit", resize: "none", maxHeight: 190, lineHeight: 1.4 }} />{/* v3.4.691 — يتمدد مع النص الجاهز */}
          <button onClick={handleSend} disabled={sending || !text.trim() || !connInfo.connected} title={!connInfo.connected ? "القناة غير متصلة — لا يمكن الإرسال الآن (اربط الرقم من إدارة الواتس)" : ""}
            style={{
              padding: "0 14px",
              background: text.trim() && !sending && connInfo.connected ? "#25d366" : "#cbd5e1",
              color: "#fff", border: "none", borderRadius: "50%",
              width: 38, height: 38, cursor: text.trim() && !sending && connInfo.connected ? "pointer" : "not-allowed",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
            {sending ? "..." : <FiSend size={16} />}
          </button>
        </div>
      </div>

      <style>{`@keyframes spin { from {transform: rotate(0)} to {transform: rotate(360deg)} } .spin { animation: spin 1s linear infinite; }`}</style>
    </div>
  );
}
